import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import inventoryService from '../../services/inventoryService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash } from '@phosphor-icons/react';

const CONDITIONS = ['new', 'good', 'fair', 'poor', 'damaged', 'disposed'];
const STATUSES = ['in_use', 'in_storage', 'under_repair', 'disposed'];

const emptyForm = {
  name: '',
  tag_number: '',
  category_id: '',
  location: '',
  condition: 'good',
  status: 'in_use',
  purchase_date: '',
  purchase_cost: '',
  notes: '',
};

function AssetsRegisterPage() {
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredAssets(assets);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredAssets(assets.filter((a) => a.name.toLowerCase().includes(term) || a.tag_number.toLowerCase().includes(term)));
  }, [assets, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assetsData, categoriesData] = await Promise.all([
        inventoryService.listAssets(),
        inventoryService.listCategories().catch(() => []),
      ]);
      setAssets(Array.isArray(assetsData) ? assetsData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => categories.find((c) => c.id === categoryId)?.name || '-';

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (asset) => {
    setEditingId(asset.id);
    setFormData({
      name: asset.name,
      tag_number: asset.tag_number,
      category_id: asset.category_id || '',
      location: asset.location || '',
      condition: asset.condition,
      status: asset.status,
      purchase_date: asset.purchase_date || '',
      purchase_cost: asset.purchase_cost || '',
      notes: asset.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.tag_number) {
      toast.error('Name and tag number are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        category_id: formData.category_id || null,
        purchase_cost: formData.purchase_cost ? Number(formData.purchase_cost) : null,
      };
      if (editingId) {
        await inventoryService.updateAsset(editingId, payload);
        toast.success('Asset updated');
      } else {
        await inventoryService.createAsset(payload);
        toast.success('Asset added');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save asset');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await inventoryService.deleteAsset(deleteTarget);
      toast.success('Asset deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete asset');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading assets...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Inventory — Assets Register</h1>
            <p>Durable, individually-tracked assets</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Add Asset
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by name or tag number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          {filteredAssets.length === 0 ? (
            <div className="empty-state">
              <p>No assets found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tag #</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Location</th>
                  <th>Condition</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map((asset) => (
                  <tr key={asset.id}>
                    <td>{asset.tag_number}</td>
                    <td>{asset.name}</td>
                    <td>{getCategoryName(asset.category_id)}</td>
                    <td>{asset.location || '-'}</td>
                    <td className="text-capitalize">{asset.condition}</td>
                    <td className="text-capitalize">{asset.status.replace('_', ' ')}</td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(asset)}>
                        <PencilSimple size={18} />
                      </button>
                      <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(asset.id)}>
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Asset' : 'Add Asset'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Tag Number *</label>
                  <input type="text" value={formData.tag_number} onChange={(e) => setFormData({ ...formData, tag_number: e.target.value })} required />
                </div>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select value={formData.category_id} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}>
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Location</label>
                <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g., Staff Room 2" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Condition *</label>
                  <select value={formData.condition} onChange={(e) => setFormData({ ...formData, condition: e.target.value })} required>
                    {CONDITIONS.map((c) => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} required>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Purchase Date</label>
                  <input type="date" value={formData.purchase_date} onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Purchase Cost (GHS)</label>
                  <input type="number" step="0.01" value={formData.purchase_cost} onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Asset"
        message="This will permanently delete this asset. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default AssetsRegisterPage;
